import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ComponentDefinition, AlarmList, ILineChartData, WidgetValues, AlarmArea, KeyValuePair, GetAggregatedValuesDto, SelectorType, IdentifierType, LogbookEntryDto, AggregationMode, IValueViewModel, TagDateValue, TagDateValuesWithRange, Dictionary } from '../../models';
import { ISeriesService, IValuesService, IDateRangeService, ILoggerService, IwidgetIntervalHelperService, IAggregatedValuesService, IGroupSelectionService, ITagsTrendService, IExportToCsvService } from '../../services';
import { IAlarmHelper, refreshClick, groupSelected, toDictionary } from '../../common/';
import { SeriesConfiguration } from '../../widgets';
import { ChartConfigurationOption } from '../../wtFramework';
import { TagsTrendConfiguration } from './tagsTrend.configuration';
import { TagsTrendWidget } from './tagsTrend.widget';
import { TrendSeries } from '@services/widgets/TrendSeries';

class TagsTrendController implements angular.IComponentController {
    alarmList: AlarmList;
    boundData: WidgetValues;
    clicked: ILineChartData;
    isLoading: boolean;
    data: TagDateValue[];
    hovered: ILineChartData;
    hoveredItem: any;
    id: string;
    item: TagsTrendWidget;
    loadingMessage: string;
    options: any;
    rootData: any;
    series: TrendSeries<TagDateValue>[];
    stateSeries: any[];

    readonly viewModels: Dictionary<IValueViewModel>;

    private _canceller: angular.IDeferred<void>;
    private _entries: LogbookEntryDto[] = [];
    private _refreshListener;
    private _seriesConfiguration: SeriesConfiguration[];

    static $inject = [
        '$scope',
        '$q',
        'exportToCsvService',
        'seriesService',
        'valuesService',
        'dateRangeService',
        'loggerService',
        'widgetIntervalHelperService',
        'aggregatedValuesService',
        'groupSelectionService',
        'tagsTrendService',
        'alarmHelper'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _exportToCsvService: IExportToCsvService,
        private _seriesService: ISeriesService,
        private _valuesService: IValuesService,
        private _dateRangeService: IDateRangeService,
        private _loggerService: ILoggerService,
        private _intervalHelper: IwidgetIntervalHelperService,
        private _aggregatedValuesService: IAggregatedValuesService,
        private _groupSelectionService: IGroupSelectionService,
        private _tagsTrendService: ITagsTrendService,
        private _alarmHelper: IAlarmHelper
    ) {
    }

    $onInit() {
        this.data = [];
        this.series = [];
        this.options = {};

        let configuration: TagsTrendConfiguration;
        try {
            this.isLoading = true;
            this.loadingMessage = 'Loading series definition';
            this.id = `id${this.item.guid}`;
            configuration = this.item.configuration;

            try {
                this.options = this.getOptions(this.item);

                this._$scope.$on(groupSelected, (event, data) => {
                    this.updateSeries()(configuration.series, []);
                });

                if (!this.boundData) {
                    if (this._dateRangeService.initialized()) {
                        const beginDate = this._dateRangeService.beginDate() as string;
                        const endDate = this._dateRangeService.endDate() as string;
                        this.options.display.xMin = moment(beginDate).toDate();
                        this.options.display.xMax = moment(endDate).toDate();
                    }
                }
                if (this.item) {
                    this.item.exportToCsv = () => {
                        return this._exportToCsvService.exportToCsv(this.series, this.data, this.item.name);
                    };
                }
            } catch (e) {
                this._loggerService.logError(e);
            }
        } catch (ex) {
            this._loggerService.logError(ex);
        }
        this.isLoading = false;

        this._$scope.$watch(() => configuration.series, (newValue, oldValue) =>
            this.updateSeries()(newValue, oldValue),
            true
        );

        this._$scope.$watch(() => configuration.nameConfiguration, (newValue, oldValue) => {
            this.refreshSeries(this._seriesConfiguration);
        }, true);

        this._$scope.$watch(() => configuration.hideMissing, () =>
            this.updateSeries()(configuration.series, configuration.series));

        this._$scope.$watch(() => configuration.showAlarms, () =>
            this.showAlarms(this.rootData, this.alarmList, configuration.showAlarms)
        );
        this._$scope.$watch(() => this.alarmList, () =>
            this.showAlarms(this.rootData, this.alarmList, configuration.showAlarms)
        );
        this._$scope.$watch(() => configuration.options, () =>
            this.options = this.getOptions(this.item),
            true);

        this.updateSeries()(configuration.series, []);

        this._$scope.$watch(() => this.boundData, () => {
            if (this.boundData) {
                const data = this._tagsTrendService.getValues(this.item.configuration, this.boundData);
                this.data = data.data;
                this.rootData = data;
                this.options.display.xMin = data.range.beginDate;
                this.options.display.xMax = data.range.endDate;
                this.updateEntries(this.boundData.entries);
                this.showAlarms(data, this.alarmList, configuration.showAlarms);
            }
        });

        //used only for group trend state
        if (this.stateSeries) {
            const newSeries: any[] = [{
                name: 'Stable',
                guid: '123456',
                identifier: `123456`,
                color: '#c9c',
                seriesType: 'area',
                y0: d => 0,
                y1: d => 1,
                isVisible: true,
                pane: 2,
                serieState: 0
            }];
            this.series = this.series.concat(newSeries);
        }
        this.legendHovered.bind(this);
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['viewModels']?.currentValue && this._seriesConfiguration) {
            this.refreshSeries(this._seriesConfiguration);
        }
    }

    showAlarms(data: TagDateValuesWithRange, alarmList: AlarmList, showAlarms: boolean) {
        if (data && alarmList && showAlarms) {
            // A bit overkill, commented to avoid passing alarmList or using redux here
            const alarms = this._alarmHelper.filterAlarmOnDataValueType(alarmList, data.data);
            const areas: AlarmArea[] = this._alarmHelper.getAlarmAreas(data.data, data.range.endDate.toISOString(), this._alarmHelper.filterAlarmOnDataValueType(alarmList, data.data));
            const lines = this._alarmHelper.getMinMaxLines(alarms, null, true);
            const firstPane = this.options.panes[0];
            if (firstPane) {
                firstPane.areas = firstPane.areas
                    ? firstPane.areas.concat(areas)
                    : areas;
                firstPane.horizontalLines = lines;
            }
        }
    }

    legendHovered(d, i) {
        this.hoveredItem = d;
        this._$scope.$apply();
    }

    private getOptions(item: TagsTrendWidget): ChartConfigurationOption {
        return this._tagsTrendService.getOptions(item);
    }

    private updateSeries() {
        return (newValue: SeriesConfiguration[], oldValue: SeriesConfiguration[]) => {
            if (newValue) {
                this.isLoading = true;
                this.loadingMessage = 'Loading series';
                const series = newValue;
                this._seriesService
                    .getUpdatedGuids(series)
                    .then((data) => {
                        this._seriesConfiguration = data;
                        return this.refreshSeries(data);
                    })
                    .catch(() => {
                        this.isLoading = false;
                    });
            }
        };
    }

    private refreshSeries(data: SeriesConfiguration[]) {
        return this._tagsTrendService
            .getSeries(
                data,
                this.item.configuration.nameConfiguration,
                this.viewModels
            )
            .then(res => {
                this.series = res.series;
                this.item.setError(res.error);
                this.isLoading = false;
                if (!this.boundData) {
                    if (this._dateRangeService.initialized()) {
                        this.refreshValues(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
                    }
                    if (!this._refreshListener) {
                        this._refreshListener =
                            this._$scope.$on(refreshClick, this.onRefreshInputValue());
                    }
                }
            });
    }

    // Missing data
    private updateMissingSeries(missing: KeyValuePair<string, any[]>[]): any[] {
        return this._seriesService.updateMissingSeries(missing);
    }

    private onRefreshInputValue() {
        return (event, data) => {
            const force = !!data.force;
            this.refreshValues(data.beginDate, data.endDate, force);
        };
    }

    private cancelPreviousRequest(): void {
        if (this._canceller) {
            this._canceller.resolve();
        }
        this._canceller = this._$q.defer();
    }

    private refreshValues(beginDate, endDate, force: boolean = false) {
        try {
            this.options.display.xMin = moment(beginDate).toDate();
            this.options.display.xMax = moment(endDate).toDate();
            this.data = [];
            this.cancelPreviousRequest();
        } catch (e) {
            // nothing
        }
        if (this._seriesConfiguration && this._seriesConfiguration.length > 0 && beginDate && endDate &&
            this._dateRangeService.initialized()) {
            this.isLoading = true;
            const updatedInterval = this._intervalHelper.getInterval(beginDate, endDate, this.item.configuration);
            const beginMoment = updatedInterval.beginMoment;
            const endMoment = updatedInterval.endMoment;
            const origEndDate = moment(endDate);
            beginDate = beginMoment.toISOString();
            endDate = endMoment.toISOString();

            try {
                this.options.display.xMin = moment(beginDate).toDate();
                this.options.display.xMax = moment.min([endMoment, origEndDate]);
                this.data = [];
            } catch (e) {
                // nothing
            }


            this.loadingMessage = 'Loading data';
            const aggregationConfiguration = this.item.configuration.aggregation;
            const series = this.item.configuration.series;

            if (!aggregationConfiguration || aggregationConfiguration.mode === AggregationMode.None) {
                let missing = [];
                this.getReadPromise(beginMoment, endMoment, force)
                    .then(data => {
                        if (data.missing) {
                            missing = this.updateMissingSeries(data.missing);
                        }
                        this.data = missing ? data.values.concat(missing) : data.values;
                        this.updateEntries(data.entries);
                    })
                    .catch(reason => {
                        if (!reason || reason.status !== -1) // aborted
                        {
                            this._loggerService.logError(reason);
                        }
                        this.isLoading = false;
                    });
            } else {
                const configuration: GetAggregatedValuesDto = {
                    beginDateTime: beginDate,
                    endDateTime: endDate,
                    aggregationInterval: aggregationConfiguration.period.interval,
                    aggregationMode: aggregationConfiguration.mode,
                    aggregationUnit: aggregationConfiguration.period.unit,
                    hasQuality: false,
                    list: series.map(r => {
                        return {
                            guid: r.guid,
                            value: (r && r.value) ? r.value : 'average'
                        };
                    })
                };
                this._aggregatedValuesService
                    .getAggregatedValuesList(configuration)
                    .then(data => {
                        this.data = data.values;
                        this.updateEntries((data as any).entries);
                    })
                    .finally(() => this.isLoading = false);
            }
        }
    }

    private getReadPromise(
        beginMoment: moment.Moment,
        endMoment: moment.Moment,
        force: boolean = false
    ): angular.IPromise<any> {
        const diff = endMoment.diff(beginMoment, 'day');
        const beginDate = beginMoment.toISOString();
        const endDate = endMoment.toISOString();
        if (diff < 30) {
            return this.read(beginDate, endDate, force)
                .then(data => {
                    this.isLoading = false;
                    return data;
                })
                .catch(reason => {
                    if (reason.exceptionType === 'System.OutOfMemoryException') {
                        this._loggerService.logError(
                            'This amount of data cannot be retrieved. Try decreasing the period you are looking at.'
                        );
                    }
                    throw reason;
                });
        } else {
            return this._valuesService
                .getCompressedValues(this._seriesConfiguration, beginDate, endDate, force, this._canceller.promise)
                .then(data => {
                    this.data = data;
                    this.isLoading = false;
                    return this.read(beginDate, endDate, force);
                    //TODO keep min and max dates in case of zoom
                })
                .catch(reason => {
                    if (reason.exceptionType === 'System.OutOfMemoryException') {
                        this._loggerService.logError(
                            'This amount of data cannot be retrieved. Try decreasing the period you are looking at.'
                        );
                    }
                    throw reason;
                });
        }
    }

    private read(beginDate, endDate, force): angular.IPromise<any> {
        return this.item.configuration.hideMissing
            ? this._valuesService
                .getValues(this._seriesConfiguration, beginDate, endDate, force, this._canceller.promise)
                .then(data => { return { values: data }; })
            : this._valuesService.getValuesWithMissing(this._seriesConfiguration, beginDate, endDate, force, this._canceller.promise);
    }

    getIdentifierTypeFromSerie(sc: any) {
        return (sc.type === SelectorType.identifier && sc.identifierType !== undefined)
            ? sc.type
            : IdentifierType.Tag;
    }

    private updateEntries(entries: LogbookEntryDto[]) {
        if (!entries) {
            entries = [];
        }

        this._entries = entries;
        this.options.panes[0].areas = this._tagsTrendService.getEntryPanes(this._entries);
        this.options.panes[0].horizontalLines = [];
    }
}

export const wtTagsTrend: ComponentDefinition = {
    componentId: 'wtTagsTrend',
    controller: TagsTrendController,
    template: require('./tagsTrend.component.html'),
    controllerAs: 'vm',
    bindings: {
        alarmList: '=?',
        boundData: '=?',
        clicked: '=',
        isLoading: '=?',
        item: '=',
        hovered: '=',
        stateSeries: '=',
        series: '=?',
        viewModels: '<'
    }
};